import PrimaryButton from "@/src/mui-custom/button/button-primary";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";
import {Box, Button, Link, Stack} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload} from "@fortawesome/pro-duotone-svg-icons";
import {fileSizeFormatter} from "@/src/utils/fileSizeFormatter";
import type ArticleBody from "@/src/_types/Article/article-body";
import type ArticleFiles from "@/src/_types/Article/article-files";
import {type ArticleUrlProps} from "@/src/_types/Article/article-sidebar";
import NextLink from "next/link";

type ArticleDownloadBoxProps = {
    data: Partial<ArticleFiles & ArticleBody>;
}

const ArticleDownloadBox = (
    {
        data,
        articleUrl
    }: {
        data: ArticleDownloadBoxProps['data'];
        articleUrl: ArticleUrlProps
    }
) => (
    <>
        <Link
            component={NextLink}
            href={`${articleUrl.path}.pdf`}
            target="_blank"
        >
            <PrimaryButton
                sx={{
                    p: .75,
                    borderRadius: 2,
                    borderBottom: '4px solid #ad934b',
                    width: '100%',
                    mb: 1
                }}
            >
                <Stack
                    component="span"
                    flexDirection="row"
                    alignItems="center"
                >
                    <SpanTypography fontSize={32} mr={.5}>
                        <FontAwesomeIcon
                            icon={faDownload}
                        />
                    </SpanTypography>
                    <Stack
                        component="span"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        <SpanTypography
                            fontWeight="bold"
                            textTransform="uppercase"
                            sx={{whiteSpace: 'nowrap'}}
                        >
                            Download PDF
                        </SpanTypography>
                        {
                            data?.pdf_file_size &&
                            <SpanTypography
                                fontSize={'.8rem'}
                                sx={{whiteSpace: 'nowrap'}}
                            >
                                {
                                    fileSizeFormatter(data?.pdf_file_size as number)
                                }
                            </SpanTypography>
                        }
                    </Stack>
                </Stack>
            </PrimaryButton>
        </Link>
        {
            data?.feature_audio?.map((fa: any, index: number) => (
                <Box key={`audio-${index}`} mb={2}>
                    {fa.size && (
                        <Link
                            component={NextLink}
                            href={process.env.NEXT_PUBLIC_SERVICE_URI_CDN_DL + fa.url} underline="none">
                            <Button
                                variant="contained"
                                startIcon={<FontAwesomeIcon icon={faDownload}/>}
                            >
                                Feature Audio File ({fileSizeFormatter(fa.size)})
                            </Button>
                        </Link>
                    )}
                </Box>
            ))
        }
        {
            data?.feature_video?.map((fv: any, index: number) => (
                <Box key={`video-${index}`} mb={2}>
                    {fv.size && (
                        <Link
                            component={NextLink}
                            href={process.env.NEXT_PUBLIC_SERVICE_URI_CDN_DL + fv.url} underline="none">
                            <Button
                                variant="contained"
                                startIcon={<FontAwesomeIcon icon={faDownload}/>}
                            >
                                Feature Video File ({fileSizeFormatter(fv.size)})
                            </Button>
                        </Link>
                    )}
                </Box>
            ))
        }
        {
            data?.feature_slide?.map((fs: any, index: number) => (
                <Box key={`slide-${index}`} mb={2}>
                    {fs.size && (
                        <Link
                            component={NextLink}
                            href={process.env.NEXT_PUBLIC_SERVICE_URI_CDN_DL + fs.url} underline="none">
                            <Button
                                variant="contained"
                                startIcon={<FontAwesomeIcon icon={faDownload}/>}
                            >
                                Feature Slide File ({fileSizeFormatter(fs.size)})
                            </Button>
                        </Link>
                    )}
                </Box>
            ))
        }
        {
            data?.supplementary_material?.[0] && (
                <Box
                    key="supplementary-0" 
                    mb={.5}
                >
                    <Link href={data.supplementary_material[0].url ?? '#'}>
                        <Button
                            sx={{
                                width: '100%',
                                padding: '.75rem',
                                color: '#484641',
                                backgroundColor: '#d0e0c4',
                                border: '1px solid rgba(155,189,129,0.5)',
                                borderRadius: '6px',
                                boxShadow: '0 1px 1px rgba(51,51,51,0.1)'
                            }}
                        >
                            <SpanTypography mr={.25}>
                                <FontAwesomeIcon icon={faDownload}/>
                            </SpanTypography>
                            <SpanTypography
                                color="#484641"
                                fontSize={'.7rem'}
                                textTransform="capitalize"
                            >
                                Download Supplementary Material
                            </SpanTypography>
                        </Button>
                    </Link>
                </Box>
            )
        }
        {
            data?.has_abstract_xml && (
                <Box mb={2}>
                    <Link href={`${process.env.NEXT_PUBLIC_SERVICE_URI_CDN_DL ?? ''}${data.abstract_xml_url || ''}`} style={{
                        textDecoration: 'none'
                    }}>
                        <Button
                            variant="contained"
                            startIcon={<FontAwesomeIcon icon={faDownload}/>}
                        >
                            Abstract XML
                        </Button>
                    </Link>
                </Box>
            )
        }
        {
            data?.opr_pdf_size && (
                <Box mb={2}>
                    <Link href={data.opr_pdf || ''} style={{
                        textDecoration: 'none'
                    }}>
                        <Button
                            variant="contained"
                            startIcon={<FontAwesomeIcon icon={faDownload}/>}
                        >
                            Peer Review Comments ({fileSizeFormatter(data?.opr_pdf_size as number)})
                        </Button>
                    </Link>
                </Box>
            )
        }
    </>
);

export default ArticleDownloadBox;